<!-- filepath: /Users/minus4/Kod/Bitbucket/new-muzible-www-login/src/views/chat/MessageItem.vue -->
<template>
  <div 
    class="message-item"
    :class="{ 
      'own-message': isOwn, 
      'grouped': isGrouped,
      'other-message': !isOwn 
    }"
    :data-message-id="message._id"
  >
    <!-- Avatar (only for first message in group) -->
    <div v-if="showAvatar && !isOwn" class="message-avatar">
      <div class="avatar-circle" :title="getFullSenderName(message)">
        {{ getInitials(message) }}
      </div>
    </div>
    
    <div class="message-content">
      <!-- Sender name (only for first message in group and group chats) -->
      <div 
        v-if="showAvatar && !isOwn && isGroupChat" 
        class="sender-name"
      >
        {{ getSenderName(message) }}
      </div>

      <!-- Message bubble -->
      <div 
        class="message-bubble" 
        @contextmenu="showContextMenu" 
        @click="showContextMenu"
      >
        <!-- Reply indicator -->
        <transition name="slide-right">
          <div v-if="message.replyTo" class="reply-indicator">
            <div class="reply-icon">
              <v-icon size="14">mdi-reply</v-icon>
            </div>
            <div class="reply-details">
              <div class="reply-label">Reply to:</div>
              <div class="reply-text">{{ getReplyPreview(message) }}</div>
            </div>
          </div>
        </transition>
        
        <!-- Message text -->
        <div class="message-text">
          {{ message.content }}
        </div>
        
        <!-- Message metadata -->
        <div class="message-meta">
          <span class="message-time">
            <v-icon size="12">mdi-clock-outline</v-icon>
            {{ formatTime(message.createdAt) }}
          </span>
          
          <!-- Seen indicators for own messages -->
          <div v-if="isOwn && message.seenBy" class="seen-indicators">
            <span 
              v-if="message.seenBy.length > 1"
              class="seen-count"
              :title="getSeenByText()"
            >
              <v-icon size="14" color="success">mdi-check-all</v-icon>
            </span>
            <span v-else class="sent-indicator">
              <v-icon size="14">mdi-check</v-icon>
            </span>
          </div>
        </div>
      </div>
      
      <!-- Reactions -->
      <transition name="fade">
        <div v-if="message.reactions && message.reactions.length > 0" class="reactions">
          <div 
            v-for="reaction in groupedReactions"
            :key="reaction.emoji"
            @click="toggleReaction(reaction.hasOwn, reaction.emoji)"
            class="reaction-item"
            :class="{ 'own-reaction': reaction.hasOwn }"
            :title="reaction.users.join(', ')"
          >
            <span class="reaction-emoji">{{ reaction.emoji }}</span>
            <span class="reaction-count">{{ reaction.count }}</span>
          </div>
          
          <!-- Add reaction button -->
          <button 
            v-if="!isOwn"
            @click="showReactionPicker = !showReactionPicker" 
            class="add-reaction-btn visible"
            title="Add Reaction"
          >
            <v-icon size="14">mdi-emoticon-plus-outline</v-icon>
          </button>
        </div>
      
      <!-- No reactions but show add button on hover -->
      <div v-else class="reactions-placeholder">
        <button 
          v-if="!isOwn"
          @click="showReactionPicker = !showReactionPicker" 
          class="add-reaction-btn"
          title="Add Reaction"
        >
          <v-icon size="14">mdi-emoticon-plus-outline</v-icon>
        </button>
      </div>
      </transition>
    </div>
    
    <!-- Context Menu -->
    <transition name="scale-fade">
      <div 
        v-if="showContextMenuFlag"
        v-click-outside="hideContextMenu"
        :class="isOwn ? 'context-menu-own' : 'context-menu'"
        :style="contextMenuStyle"
      >
        <button @click="replyToMessage" class="context-menu-item">
          <v-icon size="18">mdi-reply</v-icon>
          <span>Reply</span>
        </button>
        <button @click="copyMessage" class="context-menu-item">
          <v-icon size="18">mdi-content-copy</v-icon>
          <span>Copy</span>
        </button>
        <button 
          v-if="isOwn"
          @click="deleteMessage" 
          class="context-menu-item delete"
        >
          <v-icon size="18" color="error">mdi-delete</v-icon>
          <span>Delete</span>
        </button>
      </div>
    </transition>
    
    <!-- Reaction Picker -->
    <transition name="scale-fade">
      <div 
        v-if="showReactionPicker"
        v-click-outside="closeReactionPicker"
        class="reaction-picker"
      >
        <div class="reaction-picker-header">
          <span class="picker-title">Quick Reactions</span>
          <button @click="closeReactionPicker" class="btn-close-picker">
            <v-icon size="14">mdi-close</v-icon>
          </button>
        </div>
        <div class="reaction-grid">
          <button 
            v-for="emoji in quickReactions"
            :key="emoji"
            @click="addReaction(emoji)"
            class="reaction-option"
            :title="emoji"
          >
            {{ emoji }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'MessageItem',
  props: {
    message: {
      type: Object,
      required: true
    },
    isGrouped: {
      type: Boolean,
      default: false
    },
    showAvatar: {
      type: Boolean,
      default: true
    },
    isOwn: {
      type: Boolean,
      default: false
    },
    isGroupChat: {
      type: Boolean,
      default: false
    }
  },
  emits: ['react', 'delete', 'reply'],
  data() {
    return {
      showContextMenuFlag: false,
      showReactionPicker: false,
      showAddReaction: true,
      contextMenuStyle: {},
      
      quickReactions: ['👍', '❤️', '😂', '😮', '😢', '😡', '🔥', '⭐'],
      
      currentUserId: null
    }
  },
  computed: {
    getCurrentUserId() {
      const userData = this.$store.getters.getUserData
      return userData?.id || null
    },
    groupedReactions() {
      if (!this.message.reactions) return []
      
      const grouped = {}
      const userId = this.getCurrentUserId 
      
      this.message.reactions.forEach(reaction => {
        if (!grouped[reaction.reaction]) {
          grouped[reaction.reaction] = {
            emoji: reaction.reaction,
            count: 0,
            users: [],
            hasOwn: false
          }
        }
        
        grouped[reaction.reaction].count++
        if (reaction.userVerseUsername && reaction.userVerseUsername.length > 0){
            grouped[reaction.reaction].users.push(`${reaction.userVerseUsername}`);
        } else {
            grouped[reaction.reaction].users.push( `${reaction.userId}`);
        }

        if (reaction.userId === userId) {
          grouped[reaction.reaction].hasOwn = true
        }
      })
      
      return Object.values(grouped)
    }
  },
  mounted() {
    this.currentUserId = this.getCurrentUserId
  },
  methods: {
    formatTime(timestamp) {
      const date = new Date(timestamp)
      const now = new Date()
      
      // Same day - show time only
      if (date.toDateString() === now.toDateString()) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
      
      // Yesterday
      const yesterday = new Date(now)
      yesterday.setDate(yesterday.getDate() - 1)
      if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
      
      // Older - show date and time
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    getInitials(message) {
      const userId = message.senderId
      const verse_un = message.senderVerseUsername? message.senderVerseUsername : ''
      if (verse_un.length >= 2) {
        return verse_un.substring(0, 2).toUpperCase();
      }
      return userId.substring(0, 2).toUpperCase();
    },
    
    getFullInitials(message) {
      const userId = message.senderId
      const verse_un = message.senderVerseUsername? message.senderVerseUsername : ''
      if (verse_un.length >= 2) {
        return verse_un;
      }
      return userId;
    },
    
    getFullSenderName(message){
      const userId = message.senderId
      const verse_un = message.senderVerseUsername? message.senderVerseUsername : ''
      if (verse_un.length > 0) {
        return verse_un
      }
      return message.senderId
    },

    getSenderName(message) {
      const userId = message.senderId
      const verse_un = message.senderVerseUsername? message.senderVerseUsername : ''
      if (verse_un.length > 0) {
        return verse_un.length > 20 ? verse_un.substring(0, 20) + '...' : verse_un
      }
      return message.senderId.length > 20 ? message.senderId.substring(0, 20) + '...' : message.senderId
    },

    getSeenByText() {
      const seenCount = this.message.seenBy?.length || 0
      if (seenCount <= 1) return 'Sent'
      return `Seen by ${seenCount - 1} people`
    },

    getReplyPreview(message) {
      if (message.replyContent) {
        const content = message.replyContent.content || ''
        return content.length > 50 ? content.substring(0, 50) + '...' : content
      }
      return 'Unknown message'
    },

    showContextMenu(event) {
      event.preventDefault()
      
      this.contextMenuStyle = {
        left: event.pageX + 'px',
        top: event.pageY + 'px'
      }
      
      this.showContextMenuFlag = true
    },

    hideContextMenu() {
      this.showContextMenuFlag = false
    },

    closeReactionPicker() {
      this.showReactionPicker = false
    },

    toggleReaction(hasOwn, emoji) {
      if (hasOwn){
        this.$emit('react', this.message._id, null)
      }
    },

    addReaction(emoji) {
      this.$emit('react', this.message._id, emoji)
      this.showReactionPicker = false
    },

    replyToMessage() {
      this.$emit('reply', this.message)
      this.hideContextMenu()
    },

    deleteMessage() {
      this.$emit('delete', this.message._id)
      this.hideContextMenu()
    },

    copyMessage() {
      navigator.clipboard.writeText(this.message.content)
      this.hideContextMenu()
    },
  }
}
</script>

<style lang="scss" scoped>
@use '../../views/css/styles.scss';

.message-item {
  display: flex;
  gap: 12px;
  padding: 4px 24px;
  position: relative;
  animation: slideIn 0.3s ease;
  transition: all 0.2s ease;
}

.message-item:hover {
  background: rgba(255, 255, 255, 0.02);
}

.message-item.grouped {
  padding-top: 2px;
  padding-bottom: 2px;
}

.message-item.own-message {
  flex-direction: row-reverse;
}

.message-item.own-message .message-content {
  align-items: flex-end;
}

/* Avatar */
.message-avatar {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  margin-top: 4px;
}

.avatar-circle {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: linear-gradient(135deg, #007bff, #0056b3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  font-weight: 700;
  font-family: "Darker Grotesque", sans-serif;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.4);
  cursor: pointer;
  transition: all 0.3s ease;
}

.avatar-circle:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(0, 123, 255, 0.5);
}

/* Message Content */
.message-content {
  display: flex;
  flex-direction: column;
  max-width: 70%;
  min-width: 0;
  flex: 1;
}

.sender-name {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 4px;
  padding-left: 14px;
  font-family: "Darker Grotesque", sans-serif;
}

/* Message Bubble */
.message-bubble {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 10px 14px;
  border-radius: 16px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  max-width: 100%;
  overflow: hidden;
}

.message-bubble:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.own-message .message-bubble {
  background: linear-gradient(135deg, #007bff, #0056b3);
  border-color: rgba(255, 255, 255, 0.2);
  color: white;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.4);
}

.own-message .message-bubble:hover {
  background: linear-gradient(135deg, #0056b3, #003d82);
  box-shadow: 0 6px 16px rgba(0, 123, 255, 0.5);
}

/* Reply Indicator */
.reply-indicator {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.2);
  border-left: 3px solid rgba(255, 255, 255, 0.5);
  border-radius: 8px;
  margin-bottom: 8px;
  animation: slideRight 0.3s ease;
}

.own-message .reply-indicator {
  background: rgba(255, 255, 255, 0.15);
  border-left-color: white;
}

.reply-icon {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.7);
}

.reply-details {
  flex: 1;
  min-width: 0;
}

.reply-label {
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  font-family: "Darker Grotesque", sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.reply-text {
  font-size: 13px;
  font-family: "Darker Grotesque", sans-serif;
  color: rgba(255, 255, 255, 0.8);
  font-style: italic;
  line-height: 1.4;
  word-break: break-word;
}

/* Message Text */
.message-text {
  font-size: 15px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;
  font-family: "Darker Grotesque", sans-serif;
  color: white;
  max-width: 100%;
  overflow: hidden;
}

/* Message Meta */
.message-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  justify-content: flex-end;
}

.own-message .message-meta {
  justify-content: flex-start;
}

.message-time {
  font-size: 11px;
  font-family: "Darker Grotesque", sans-serif;
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  gap: 4px;
}

.seen-indicators {
  display: flex;
  align-items: center;
}

.sent-indicator,
.seen-count {
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
}

/* Reactions */
.reactions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 0 0 14px;
  min-height: 28px;
  align-items: center;
}

.own-message .reactions {
  justify-content: flex-end;
  padding-left: 0;
  padding-right: 14px;
}

.reaction-item {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 14px;
  padding: 4px 8px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.3s ease;
  animation: scaleIn 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.reaction-item:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.reaction-item.own-reaction {
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  border-color: rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.4);
}

.reaction-item.own-reaction:hover {
  background: linear-gradient(135deg, #0056b3, #003d82);
  box-shadow: 0 6px 16px rgba(0, 123, 255, 0.5);
}

.reaction-emoji {
  font-size: 14px;
  line-height: 1;
}

.reaction-count {
  font-size: 11px;
  font-weight: 600;
  font-family: "Darker Grotesque", sans-serif;
}

/* Reactions Placeholder */
.reactions-placeholder {
  min-height: 28px;
  padding: 4px 0 0 14px;
  display: flex;
  align-items: center;
}

.own-message .reactions-placeholder {
  padding-left: 0;
  padding-right: 14px;
  justify-content: flex-end;
}

/* Add Reaction Button */
.add-reaction-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 14px;
  padding: 4px 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: none;
  opacity: 0;
  color: rgba(255, 255, 255, 0.6);
}

.message-item:hover .add-reaction-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  animation: fadeIn 0.3s ease;
}

.add-reaction-btn.visible {
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
}

.add-reaction-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  color: white;
  transform: scale(1.1);
}

/* Context Menu */
.context-menu,
.context-menu-own {
  position: fixed;
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.98), rgba(18, 18, 18, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  z-index: 1000;
  min-width: 140px;
  overflow: hidden;
  backdrop-filter: blur(10px);
  animation: scaleIn 0.2s ease;
}

.context-menu-own {
  transform: translateX(-100%);
}

.context-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: transparent;
  text-align: left;
  font-size: 14px;
  font-family: "Darker Grotesque", sans-serif;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.context-menu-item:last-child {
  border-bottom: none;
}

.context-menu-item:hover {
  background: rgba(255, 255, 255, 0.1);
  padding-left: 18px;
}

.context-menu-item.delete {
  color: #dc3545;
}

.context-menu-item.delete:hover {
  background: rgba(220, 53, 69, 0.2);
}

/* Reaction Picker */
.reaction-picker {
  position: absolute;
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.98), rgba(18, 18, 18, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  z-index: 1000;
  top: 100%;
  left: 0;
  margin-top: 4px;
  backdrop-filter: blur(10px);
  animation: scaleIn 0.2s ease;
}

.own-message .reaction-picker {
  right: 0;
  left: auto;
}

.reaction-picker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.picker-title {
  font-size: 13px;
  font-weight: 600;
  font-family: "Darker Grotesque", sans-serif;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.btn-close-picker {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.btn-close-picker:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.4);
}

.reaction-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}

.reaction-option {
  font-size: 24px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  text-align: center;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reaction-option:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
  transform: scale(1.2);
}

/* Animations */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideRight {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Transition Classes */
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.3s ease;
}

.slide-right-enter-from,
.slide-right-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.scale-fade-enter-active,
.scale-fade-leave-active {
  transition: all 0.2s ease;
}

.scale-fade-enter-from,
.scale-fade-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

/* Responsive */
@media (max-width: 768px) {
  .message-item {
    padding: 4px 20px;
  }

  .message-content {
    max-width: 80%;
  }

  .message-bubble {
    padding: 8px 12px;
  }

  .message-text {
    font-size: 14px;
  }

  .avatar-circle {
    width: 36px;
    height: 36px;
    font-size: 13px;
  }

  .reaction-grid {
    grid-template-columns: repeat(4, 1fr);
  }

  .reaction-option {
    font-size: 20px;
    padding: 6px;
  }

  .context-menu,
  .context-menu-own {
    min-width: 120px;
  }
}

/* Vuetify Overrides */
:deep(.v-icon) {
  color: inherit !important;
}
</style>